import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { type CalendarDate, getNumberOfDaysInInterval, isAfter, isBefore, toCalendarDate } from './dateutils';
import { roundAtSecondDecimal } from './numbers';
import { type Invoice, type Lease } from './types';

export const URGENT_OVERDUE_IN_DAYS = 60;

export interface OverdueLease {
  leaseId: string;
  amount: number;
  daysLate: number;
  invoiceCount: number;
}

export interface OverdueFigures {
  amount: number;
  leaseCount: number;
  invoiceCount: number;
  maxDaysLate: number;
  urgentLeaseCount: number;
  leases: OverdueLease[];
}

interface OverdueInvoice {
  amount: number;
  referenceDate: CalendarDate;
}

export interface OverdueInput {
  leases: Lease[];
  unpaidRentInvoices: Invoice[];
  at: CalendarDate;
}

export const EMPTY_OVERDUE: OverdueFigures = {
  amount: 0,
  leaseCount: 0,
  invoiceCount: 0,
  maxDaysLate: 0,
  urgentLeaseCount: 0,
  leases: [],
};

export const getInvoiceReferenceDate = (invoice: Invoice): CalendarDate =>
  toCalendarDate(invoice.dueDate ?? invoice.invoiceDate);

const isInvoiceOverdueAt = (invoice: Invoice, referenceDate: CalendarDate, at: CalendarDate): boolean => {
  if (invoice.creditNote === true) {
    return false;
  }
  return isAfter(at, referenceDate);
};

export const isInvoiceOverdue = (invoice: Invoice, at: CalendarDate): boolean =>
  isInvoiceOverdueAt(invoice, getInvoiceReferenceDate(invoice), at);

const getOwedAmount = (lease: Lease): number => Math.max(0, -(lease.balance ?? 0));

export const computeOverdueForLease = (
  lease: Lease,
  unpaidRentInvoices: Invoice[],
  at: CalendarDate
): OverdueLease | null => {
  const owedAmount = getOwedAmount(lease);
  if (owedAmount <= 0) {
    return null;
  }
  const overdueInvoices = unpaidRentInvoices.reduce((acc: OverdueInvoice[], invoice) => {
    if (invoice.leaseId !== lease.id) {
      return acc;
    }
    const referenceDate = getInvoiceReferenceDate(invoice);
    if (isInvoiceOverdueAt(invoice, referenceDate, at)) {
      acc.push({ amount: invoice.amount ?? 0, referenceDate });
    }
    return acc;
  }, []);
  if (isEmpty(overdueInvoices)) {
    return null;
  }
  const invoicedAmount = overdueInvoices.reduce((total: number, entry) => total + entry.amount, 0);
  const amount = roundAtSecondDecimal(Math.min(invoicedAmount, owedAmount));
  if (amount <= 0) {
    return null;
  }
  const earliestReferenceDate = overdueInvoices.reduce(
    (earliest: CalendarDate, entry) => (isBefore(entry.referenceDate, earliest) ? entry.referenceDate : earliest),
    overdueInvoices[0].referenceDate
  );
  const daysLate = getNumberOfDaysInInterval({ start: earliestReferenceDate, end: at }, false);
  return { leaseId: lease.id, amount, daysLate, invoiceCount: overdueInvoices.length };
};

export const computeOverdue = ({ leases, unpaidRentInvoices, at }: OverdueInput): OverdueFigures => {
  const invoicesByLease = groupBy(unpaidRentInvoices, 'leaseId');
  const overdueLeases = leases.reduce((acc: OverdueLease[], lease) => {
    const overdueLease = computeOverdueForLease(lease, invoicesByLease[lease.id] ?? [], at);
    if (!isNil(overdueLease)) {
      acc.push(overdueLease);
    }
    return acc;
  }, []);
  if (isEmpty(overdueLeases)) {
    return EMPTY_OVERDUE;
  }
  return overdueLeases.reduce(
    (figures: OverdueFigures, overdueLease) => ({
      amount: roundAtSecondDecimal(figures.amount + overdueLease.amount),
      leaseCount: figures.leaseCount + 1,
      invoiceCount: figures.invoiceCount + overdueLease.invoiceCount,
      maxDaysLate: Math.max(figures.maxDaysLate, overdueLease.daysLate),
      urgentLeaseCount: figures.urgentLeaseCount + (overdueLease.daysLate >= URGENT_OVERDUE_IN_DAYS ? 1 : 0),
      leases: overdueLeases,
    }),
    { ...EMPTY_OVERDUE, leases: overdueLeases }
  );
};
